import { useEffect, useRef, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { match } from 'ts-pattern';

import {
  BedrockAgentType,
  TBedrockAgentType,
} from '@cloud-wave/neon-common-lib';

import { useConfigContext } from 'lib/core/config';
import { useAuthContext } from 'lib/core/context/AuthProvider';

import { IQResponseState, TIQState } from '../components/constants/IQResponseState';
import { TStreamingState, TUseIQAPIResponse } from '../types/contextAndHookTypes';
import {
  TExtractCorrectAgentResponse,
  TIQActionFetchResponse,
  TIQFetchInternalParams,
  TRefineAgentResponse,
} from '../types/iqActionFetch';
import { readNdjsonStream } from '../utils/readNdjsonStream';

const CLEAR = 'clear';
type TClear = typeof CLEAR;

const EMPTY_STREAMING_STATE: TStreamingState = {};

const getActionEndpointName = (action: TBedrockAgentType) => {
  return match(action).otherwise(() => `${action.replace('AI_', '').toLocaleLowerCase()}-stream`);
};

const stripEmptyQuotes = (value: string): string => {
  return value.replace(/^"|"$/g, '');
};

const buildResponseFromStream = (
  accumulated: TStreamingState,
  action: TBedrockAgentType,
): TIQActionFetchResponse => {
  if (action === BedrockAgentType.AI_REFINE) {
    return {
      // profanity mode might return empty string, replacing it with '' so that we can show no suggested rewrites
      suggestedRewrite: stripEmptyQuotes(accumulated.rewrite ?? ''),
      critique: accumulated.feedback ?? []
    } as unknown as TRefineAgentResponse;
  }

  return { output: accumulated.output ?? '' } as unknown as TExtractCorrectAgentResponse;
};

const normaliseStreamingState = (state: TStreamingState): TStreamingState => ({
  ...state,
  rewrite: state.rewrite !== undefined ? stripEmptyQuotes(state.rewrite) : undefined,
});

export const useIQApi = () => {
  const { fetch_ } = useAuthContext();
  const { config } = useConfigContext();

  const { AI_SERVICE_URL, TENANT_ID } = config;
  const aiTenantURl = `${AI_SERVICE_URL}/agent/${TENANT_ID}`;

  const [requestState, setRequestState] = useState<TIQState>(IQResponseState.STANDBY);
  const [response, setResponse] = useState<TIQActionFetchResponse | null>(null);
  const [lastRequest, setLastRequest] = useState<TIQFetchInternalParams | null>(null);
  const [streamingState, setStreamingState] = useState<TStreamingState>(EMPTY_STREAMING_STATE);
  const abortControllerRef = useRef<AbortController | null>(null);

  // abort any active requests on unmount
  useEffect(() => abortActiveRequest, []);

  const resetIQApiState = () => {
    abortActiveRequest();
    setLastRequest(null);
    setRequestState(IQResponseState.STANDBY);
    setResponse(null);
    setStreamingState(EMPTY_STREAMING_STATE);
  };

  const setAbortController = (clear?: TClear) => {
    abortControllerRef.current = clear ? null : new AbortController();
  };

  const abortActiveRequest = () => {
    abortControllerRef?.current && abortControllerRef.current.abort();
  };

  const mutationFn = async (params: TIQFetchInternalParams) => {
    abortActiveRequest();
    setAbortController();

    if (!params.rethink) {
      setStreamingState(EMPTY_STREAMING_STATE);
    }

    const { action, body } = params;

    const aiAgentUrl = `${aiTenantURl}/${getActionEndpointName(action)}`;

    setLastRequest(params);

    const fetchResponse = await fetch_(aiAgentUrl, {
      body: JSON.stringify(body),
      method: 'POST',
      signal: abortControllerRef?.current?.signal,
    });

    if (!fetchResponse.ok) {
      throw new Error(`Failed to call ${action} agent: ${fetchResponse.status} ${fetchResponse.statusText}`);
    }

    if (!fetchResponse.body) {
      throw new Error(`No response body from ${action} agent`);
    }

    const onStreamUpdate = params.rethink
      ? (state: TStreamingState) => setStreamingState((prev) => ({
        ...prev,
        rewrite: normaliseStreamingState(state).rewrite,
        output: state.output
      }))
      : (state: TStreamingState) => setStreamingState(normaliseStreamingState(state));

    const accumulated = await readNdjsonStream(fetchResponse.body, onStreamUpdate, params.rethink);

    return buildResponseFromStream(accumulated, action);
  };

  const onSuccess = (newResponse: TIQActionFetchResponse, { rethink, action }: TIQFetchInternalParams) => {
    setAbortController(CLEAR);

    const responseStateLogic = (prevState: TIQActionFetchResponse | null) => {
      if (!rethink || !prevState) {
        return newResponse;
      }

      const override = match(action)
        .with(BedrockAgentType.AI_REFINE, () => ({
          suggestedRewrite: (newResponse as TRefineAgentResponse).suggestedRewrite,
        }))
        .otherwise(() => ({ output: (newResponse as TExtractCorrectAgentResponse).output }));

      return { ...prevState, ...override } as TIQActionFetchResponse;
    };

    setResponse(responseStateLogic);
    setRequestState(IQResponseState.STANDBY);
  };

  const onError = (error: unknown) => {
    setAbortController(CLEAR);

    const isAbort = error instanceof DOMException && error.name === 'AbortError';
    setRequestState(isAbort ? IQResponseState.STANDBY : IQResponseState.ERROR);
  };

  const { mutateAsync: callIQServiceAction } = useMutation({
    mutationFn,
    onMutate: ({ rethink }) => setRequestState(rethink ? IQResponseState.RETHINKING : IQResponseState.THINKING),
    onSuccess,
    onError,
    retry: false,
  });

  // used for both rethink and retrying failed requests
  const reCallLastIQServiceAction = (rethink?: boolean) => {
    if (!lastRequest) {
      throw new Error('missing last request');
    }

    if (rethink && lastRequest.action === BedrockAgentType.AI_REFINE) {
      return callIQServiceAction({
        ...lastRequest,
        body: { ...lastRequest.body, withEvaluation: false },
        rethink
      });
    }

    return callIQServiceAction({ ...lastRequest, rethink });
  };

  return {
    state: { requestState, response, streamingState },
    actions: {
      callIQServiceAction,
      reCallLastIQServiceAction,
      resetIQApiState,
      abortActiveRequest,
    },
  } satisfies TUseIQAPIResponse;
};
